import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { CompiledStage } from "../terrain/compile-rules.ts";
import { compileFace, extendFaces, type CompiledFace } from "../terrain/faces.ts";
import { applyRuleStage, type CellRect, type StageTally } from "../terrain/match.ts";
import { buildCornerIndex, resolveCornerLayer, type CornerIndex } from "../terrain/resolve.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { rerunOrderedStage, type PriorCells } from "./ordered-rerun.ts";

/**
 * Incremental terrain updates for the editor (ADR-020): one session per terrain layer keeps the corner
 * resolver's output and every stage's output as full-size layers, so a stroke re-resolves only the cells it
 * touched and re-runs each stage over the rectangle that stage could have changed. A terrain that declares a
 * face runs the face pass (ADR-072 decision 3) as one more unordered stage after the last rule stage, at the
 * session's mutable `faceHeight`. The result after every update is byte-identical to a full pipeline run over
 * the same corners at the same height. An update keeps nothing between calls but the layers themselves: what
 * it recomputes is worked out from the vertices it is given and the stages' extents every time, and each
 * stage hands the next the cells it changed as they were, in a buffer the size of that change.
 */

/**
 * What a session needs.
 */
export interface TerrainSessionInput {
  readonly terrain: TerrainDefinition;
  readonly tilesetIndex: number;
  readonly width: number;
  readonly height: number;
  readonly corners: Uint8Array;
  readonly seed: number;
  readonly layerIndex: number;
  readonly stages: readonly CompiledStage[];
  readonly stageIndexBase: number;
}

/**
 * The extent of any rule's reads and writes relative to its position, per stage.
 */
export interface StageExtent {
  readonly minDx: number;
  readonly minDy: number;
  readonly maxDx: number;
  readonly maxDy: number;
}

/**
 * The work a session's last update did: `resolved` is the number of cells the corner resolver wrote,
 * `tested` the number of rule positions its stage reruns tried (one for every active rule at every candidate
 * position) and `drawn` the number of probability draws they made. All restart at zero with every update.
 */
export interface SessionWork extends StageTally {
  resolved: number;
}

/**
 * A live session. `corners` is the buffer strokes edit; `snapshots` hold the resolver's and every rule
 * stage's layer; `output` is the face pass's layer when the terrain declares a face (`face`), otherwise the
 * last rule stage's, and keeps its identity for the session's life; `faceHeight` is the height the face pass
 * draws, the face's own rows until `setSessionFaceHeight` changes it (0 without a face); `scratch` is a
 * full-size buffer for a stage that must be run whole; `work` counts what the last
 * update recomputed.
 */
export interface TerrainSession {
  readonly input: TerrainSessionInput;
  readonly corners: Uint8Array;
  readonly index: CornerIndex;
  readonly extents: readonly StageExtent[];
  readonly snapshots: readonly Uint32Array[];
  readonly face: CompiledFace | null;
  faceHeight: number;
  readonly output: Uint32Array;
  readonly scratch: Uint32Array;
  readonly work: SessionWork;
}

/**
 * Changed vertex bounds, inclusive.
 */
export interface VertexBounds {
  readonly vx0: number;
  readonly vy0: number;
  readonly vx1: number;
  readonly vy1: number;
}

/**
 * Computes how far any rule of a stage reads or writes from its position: the union of input boxes and
 * output cells, as offsets. `maxDx` and `maxDy` are exclusive.
 *
 * @param stage Compiled stage.
 * @returns The extent; a stage with no active rules covers nothing (`maxDx <= minDx`), and the session then
 *   passes the previous stage's cells through unchanged.
 */
export function stageExtent(stage: CompiledStage): StageExtent {
  let minDx = 0;
  let minDy = 0;
  let maxDx = 0;
  let maxDy = 0;
  for (const rule of stage.rules) {
    if (rule.disabled || rule.impossible) {
      continue;
    }
    maxDx = Math.max(maxDx, rule.inputWidth);
    maxDy = Math.max(maxDy, rule.inputHeight);
    for (const target of rule.targets) {
      for (const c of target.constraints) {
        minDx = Math.min(minDx, c.dx);
        minDy = Math.min(minDy, c.dy);
        maxDx = Math.max(maxDx, c.dx + 1);
        maxDy = Math.max(maxDy, c.dy + 1);
      }
    }
    for (const output of rule.outputs) {
      for (const c of output.cells) {
        minDx = Math.min(minDx, c.dx);
        minDy = Math.min(minDy, c.dy);
        maxDx = Math.max(maxDx, c.dx + 1);
        maxDy = Math.max(maxDy, c.dy + 1);
      }
    }
  }
  return { minDx, minDy, maxDx, maxDy };
}

/**
 * Clamps a rectangle to the map.
 *
 * @param rect Rectangle.
 * @param width Map width.
 * @param height Map height.
 * @returns The clamped rectangle (possibly empty).
 */
function clampRect(rect: CellRect, width: number, height: number): CellRect {
  const x0 = Math.max(0, rect.x0);
  const y0 = Math.max(0, rect.y0);
  return { x0, y0, x1: Math.max(x0, Math.min(width, rect.x1)), y1: Math.max(y0, Math.min(height, rect.y1)) };
}

/**
 * Copies a rectangle of cells from one full-size layer to another.
 *
 * @param from Source layer.
 * @param to Target layer.
 * @param rect Cells to copy.
 * @param width Map width.
 */
function copyRect(from: Uint32Array, to: Uint32Array, rect: CellRect, width: number): void {
  for (let y = rect.y0; y < rect.y1; y += 1) {
    const start = y * width + rect.x0;
    to.set(from.subarray(start, start + rect.x1 - rect.x0), start);
  }
}

/**
 * What a stage's rerun changed: the bounds of the cells that may differ, and the stage's layer as it was
 * over an area that covers them, for the stage that reads it.
 */
interface StageChange {
  readonly changed: CellRect;
  readonly prior: PriorCells;
}

/**
 * Copies a rectangle of cells out of a full-size layer.
 *
 * @param from The layer.
 * @param area Cells to copy.
 * @param width Map width.
 * @returns The cells, row-major over the area.
 */
function takeRect(from: Uint32Array, area: CellRect, width: number): PriorCells {
  const span = area.x1 - area.x0;
  const values = new Uint32Array(Math.max(0, span) * Math.max(0, area.y1 - area.y0));
  for (let y = area.y0; y < area.y1 && span > 0; y += 1) {
    values.set(from.subarray(y * width + area.x0, y * width + area.x1), (y - area.y0) * span);
  }
  return { area, values };
}

/**
 * Rectangle of cells of a layer that differ from what they were.
 *
 * @param layer The layer now.
 * @param prior Its cells as they were over an area.
 * @param width Map width.
 * @returns The bounds within the area, empty (`x1 <= x0`) when nothing differs.
 */
function changedFrom(layer: Uint32Array, prior: PriorCells, width: number): CellRect {
  const { area, values } = prior;
  const span = area.x1 - area.x0;
  const rect: CellRect = { x0: width, y0: area.y1, x1: 0, y1: area.y0 };
  for (let y = area.y0; y < area.y1; y += 1) {
    for (let x = area.x0; x < area.x1; x += 1) {
      if (layer[y * width + x] !== values[(y - area.y0) * span + x - area.x0]) {
        rect.x0 = Math.min(rect.x0, x);
        rect.x1 = Math.max(rect.x1, x + 1);
        rect.y0 = Math.min(rect.y0, y);
        rect.y1 = Math.max(rect.y1, y + 1);
      }
    }
  }
  return rect;
}

/**
 * Starts a session, resolving the whole layer once.
 *
 * @param input Terrain, geometry, corners, seed and compiled stages.
 * @returns The session, drawing a declared face at its own rows.
 * @throws Error when the resolver rejects the input or the terrain's face declaration is malformed.
 */
export function createTerrainSession(input: TerrainSessionInput): TerrainSession {
  const { width, height } = input;
  const corners = new Uint8Array(input.corners);
  const index = buildCornerIndex(input.terrain);
  const extents = input.stages.map(stageExtent);
  const resolved = resolveCornerLayer({ terrain: input.terrain, tilesetIndex: input.tilesetIndex, width, height, corners, seed: input.seed, layerIndex: input.layerIndex, index, output: new Uint32Array(width * height).fill(EMPTY_TILE) });
  const snapshots: Uint32Array[] = [resolved.tiles];
  input.stages.forEach((stage, k) => {
    const layer = new Uint32Array(snapshots[k] as Uint32Array);
    const layers = new Map([[input.terrain.ruleSet as string, layer]]);
    applyRuleStage(stage, { width, height, layers, seed: input.seed, layerIndex: input.layerIndex, stageIndex: input.stageIndexBase + k }, { snapshot: new Map([[input.terrain.ruleSet as string, snapshots[k] as Uint32Array]]) });
    snapshots.push(layer);
  });
  const rules = snapshots[snapshots.length - 1] as Uint32Array;
  if (input.terrain.face === undefined) {
    return { input, corners, index, extents, snapshots, face: null, faceHeight: 0, output: rules, scratch: new Uint32Array(width * height), work: { resolved: 0, tested: 0, drawn: 0 } };
  }
  const face = compileFace(input.terrain.face, input.tilesetIndex);
  return { input, corners, index, extents, snapshots, face, faceHeight: face.rows, output: new Uint32Array(rules), scratch: new Uint32Array(width * height), work: { resolved: 0, tested: 0, drawn: 0 } };
}

/**
 * Runs the face pass at the session's height over a restricted run, reading the last rule stage.
 *
 * @param session A session whose terrain declares a face.
 * @param face Its compiled face.
 * @param options `rect` and `writeRect` as `extendFaces` takes them; the whole layer when omitted.
 */
function runFace(session: TerrainSession, face: CompiledFace, options: { readonly rect?: CellRect; readonly writeRect?: CellRect } = {}): void {
  const { input, snapshots } = session;
  extendFaces({ face, source: snapshots[snapshots.length - 1] as Uint32Array, output: session.output, width: input.width, height: input.height, faceHeight: session.faceHeight, seed: input.seed, layerIndex: input.layerIndex, stageIndex: input.stageIndexBase + input.stages.length }, options);
}

/**
 * Re-runs the face pass over the cells a change to the last rule stage can reach. The pass's extent is the
 * reach of a lip at its position, `dx` 0 to 1 and `dy` 0 to `faceHeight` (exclusive), so lips are rescanned
 * from `faceHeight` less one rows above every cell the rerun covers, and it covers cells down to `faceHeight`
 * less one rows below the change; the pass resets its output to the rules' output over the covered rectangle
 * before writing, as an unordered stage's rerun does. A face with side columns (ADR-072's amendment) reaches
 * further: a side reads the columns beside it and, through the lower face it meets, `2 * faceHeight - 2`
 * rows below its lip, and writes its own column from the row below its lip to `faceHeight - 1` rows below
 * it, so the rerun covers one more column each side and `2 * faceHeight - 2` rows above the change.
 *
 * @param session Session.
 * @param face Its compiled face.
 * @param dirty Cells the last rule stage changed.
 * @returns Cells the pass may have changed.
 */
function rerunFace(session: TerrainSession, face: CompiledFace, dirty: CellRect): CellRect {
  const { width, height } = session.input;
  const reach = session.faceHeight;
  const across = face.sides.length > 0 ? 1 : 0;
  const above = face.sides.length > 0 ? 2 * reach - 2 : reach - 1;
  const covered = clampRect({ x0: dirty.x0 - across, y0: dirty.y0 - above, x1: dirty.x1 + across, y1: dirty.y1 + reach - 1 }, width, height);
  const scan = clampRect({ x0: covered.x0, y0: covered.y0 - reach + 1, x1: covered.x1, y1: covered.y1 }, width, height);
  runFace(session, face, { rect: scan, writeRect: covered });
  return covered;
}

/**
 * Sets the height the session's face pass draws and re-runs the pass alone over the whole layer from the
 * retained last rule stage, so no rule stage runs and the session keeps its buffers.
 *
 * @param session A session whose terrain declares a face.
 * @param faceHeight The height, from the face's rows to `FACE_HEIGHT_MAX`.
 * @returns The cells to repaint: the whole layer.
 * @throws RangeError when the terrain declares no face or the height is outside its face's rows to
 *   `FACE_HEIGHT_MAX`; the session is then unchanged.
 */
export function setSessionFaceHeight(session: TerrainSession, faceHeight: number): CellRect {
  const { face } = session;
  if (face === null) {
    throw new RangeError(`terrain ${session.input.terrain.id} declares no face, so its layer takes no face height`);
  }
  const previous = session.faceHeight;
  session.faceHeight = faceHeight;
  try {
    runFace(session, face);
  } catch (error) {
    session.faceHeight = previous;
    throw error;
  }
  return { x0: 0, y0: 0, x1: session.input.width, y1: session.input.height };
}

/**
 * Rectangle of cells that differ between two layers within a rectangle.
 *
 * @param a First layer.
 * @param b Second layer.
 * @param width Map width.
 * @param within The cells compared.
 * @returns The bounds, empty (`x1 <= x0`) when nothing differs.
 */
function changedRect(a: Uint32Array, b: Uint32Array, width: number, within: CellRect): CellRect {
  const rect: CellRect = { x0: width, y0: within.y1, x1: 0, y1: within.y0 };
  for (let y = within.y0; y < within.y1; y += 1) {
    for (let x = within.x0; x < within.x1; x += 1) {
      const i = y * width + x;
      if (a[i] !== b[i]) {
        rect.x0 = Math.min(rect.x0, x);
        rect.x1 = Math.max(rect.x1, x + 1);
        rect.y0 = Math.min(rect.y0, y);
        rect.y1 = Math.max(rect.y1, y + 1);
      }
    }
  }
  return rect;
}

/**
 * Re-runs a `MatchInOrder` false stage over the cells the previous stage's changes can reach. Positions
 * whose reads or writes touch the changed region are rescanned against the pre-stage layer, and every
 * position that can write into the covered rectangle is rescanned in rule order so overwrite order holds;
 * writes are clipped to that rectangle because cells outside it already hold the right values.
 *
 * @param session Session.
 * @param k Stage index within the session.
 * @param dirty Cells changed by the previous stage.
 * @returns Cells this stage changed, and its layer as it was over the cells it rewrote.
 */
function rerunUnordered(session: TerrainSession, k: number, dirty: CellRect): StageChange {
  const { input, snapshots } = session;
  const { width, height } = input;
  const extent = session.extents[k] as StageExtent;
  const previous = snapshots[k] as Uint32Array;
  const current = snapshots[k + 1] as Uint32Array;
  const target = input.terrain.ruleSet as string;
  const positions = clampRect({ x0: dirty.x0 - extent.maxDx + 1, y0: dirty.y0 - extent.maxDy + 1, x1: dirty.x1 - extent.minDx, y1: dirty.y1 - extent.minDy }, width, height);
  const covered = clampRect({ x0: Math.min(dirty.x0, positions.x0 + extent.minDx), y0: Math.min(dirty.y0, positions.y0 + extent.minDy), x1: Math.max(dirty.x1, positions.x1 - 1 + extent.maxDx), y1: Math.max(dirty.y1, positions.y1 - 1 + extent.maxDy) }, width, height);
  const scan = clampRect({ x0: covered.x0 - extent.maxDx + 1, y0: covered.y0 - extent.maxDy + 1, x1: covered.x1 - extent.minDx, y1: covered.y1 - extent.minDy }, width, height);
  const prior = takeRect(current, covered, width);
  copyRect(previous, current, covered, width);
  applyRuleStage(input.stages[k] as CompiledStage, { width, height, layers: new Map([[target, current]]), seed: input.seed, layerIndex: input.layerIndex, stageIndex: input.stageIndexBase + k }, { rect: scan, snapshot: new Map([[target, previous]]), writeRect: covered, tally: session.work });
  return { changed: changedFrom(current, prior, width), prior };
}

/**
 * Whether a stage can be replayed over a window: every constraint of its active rules reads the session's
 * layer, inside its rule's input box. The matcher keeps a position's reads inside the map only there, and a
 * rule may read another layer that an earlier rule of the same ordered stage wrote, which a replay of one
 * layer does not hold.
 *
 * @param stage Compiled stage.
 * @param target The session's layer.
 * @returns True when no active rule reads another layer or outside its input box.
 */
function replayable(stage: CompiledStage, target: string): boolean {
  return stage.rules.every((rule) => rule.disabled || rule.impossible || rule.targets.every((side) => side.constraints.length === 0 || (side.target === target && side.constraints.every((c) => c.dx >= 0 && c.dy >= 0 && c.dx < rule.inputWidth && c.dy < rule.inputHeight))));
}

/**
 * Re-runs a `MatchInOrder` true stage after the previous stage changed. The scan is rule-major, so a later
 * rule at a row above reads what an earlier rule wrote below it, and no row of the previous result can be
 * taken as the state the scan had when it reached that row: the stage is replayed from the pre-stage layer
 * over a window of positions around the change that grows until every step outside it is proven to do what
 * it did before (`rerunOrderedStage`). A stage that cannot be replayed (`replayable`), and one whose window
 * grows past `WHOLE_RUN_SHARE` of the map, is run whole.
 *
 * @param session Session.
 * @param k Stage index within the session.
 * @param dirty Cells changed by the previous stage.
 * @param prior The pre-stage layer as it was, over an area covering those cells.
 * @returns Cells this stage changed, and its layer as it was over an area covering them.
 */
function rerunOrdered(session: TerrainSession, k: number, dirty: CellRect, prior: PriorCells): StageChange {
  const { input, snapshots, scratch } = session;
  const { width, height } = input;
  const stage = input.stages[k] as CompiledStage;
  const previous = snapshots[k] as Uint32Array;
  const current = snapshots[k + 1] as Uint32Array;
  const target = input.terrain.ruleSet as string;
  const stageIndex = input.stageIndexBase + k;
  const replayed = replayable(stage, target) ? rerunOrderedStage({ stage, target, extent: session.extents[k] as StageExtent, width, height, seed: input.seed, layerIndex: input.layerIndex, stageIndex, previous, prior, current, tally: session.work }, dirty) : null;
  if (replayed !== null) {
    return replayed;
  }
  const whole = { x0: 0, y0: 0, x1: width, y1: height };
  scratch.set(current);
  current.set(previous);
  applyRuleStage(stage, { width, height, layers: new Map([[target, current]]), seed: input.seed, layerIndex: input.layerIndex, stageIndex }, { tally: session.work });
  return { changed: changedRect(current, scratch, width, whole), prior: { area: whole, values: new Uint32Array(scratch) } };
}

/**
 * Re-resolves after the session's `corners` changed inside `bounds`, and re-runs every stage over the
 * cells it could have changed.
 *
 * @param session The session; its `corners` already hold the new values.
 * @param bounds Vertices that changed, inclusive.
 * @returns The cell rectangle whose final output may differ, for repainting; empty (`x1 <= x0`) when
 *   nothing changed.
 */
export function updateTerrainSession(session: TerrainSession, bounds: VertexBounds): CellRect {
  const { input, snapshots } = session;
  const { width, height } = input;
  const resolveRect = clampRect({ x0: bounds.vx0 - 1, y0: bounds.vy0 - 1, x1: bounds.vx1 + 1, y1: bounds.vy1 + 1 }, width, height);
  const first = snapshots[0] as Uint32Array;
  session.work.resolved = (resolveRect.x1 - resolveRect.x0) * (resolveRect.y1 - resolveRect.y0);
  session.work.tested = 0;
  session.work.drawn = 0;
  let prior = takeRect(first, resolveRect, width);
  resolveCornerLayer({ terrain: input.terrain, tilesetIndex: input.tilesetIndex, width, height, corners: session.corners, seed: input.seed, layerIndex: input.layerIndex, index: session.index, output: first, rect: resolveRect });
  let dirty = changedFrom(first, prior, width);
  let repaint = dirty;
  input.stages.forEach((stage, k) => {
    if (dirty.x1 <= dirty.x0 || dirty.y1 <= dirty.y0) {
      return;
    }
    const extent = session.extents[k] as StageExtent;
    if (extent.maxDx <= extent.minDx || extent.maxDy <= extent.minDy) {
      const passed = takeRect(snapshots[k + 1] as Uint32Array, dirty, width);
      copyRect(snapshots[k] as Uint32Array, snapshots[k + 1] as Uint32Array, dirty, width);
      prior = passed;
    } else {
      const change = stage.matchInOrder ? rerunOrdered(session, k, dirty, prior) : rerunUnordered(session, k, dirty);
      dirty = change.changed;
      prior = change.prior;
    }
    repaint = union(repaint, dirty);
  });
  if (session.face !== null && dirty.x1 > dirty.x0 && dirty.y1 > dirty.y0) {
    repaint = union(repaint, rerunFace(session, session.face, dirty));
  }
  return repaint;
}

/**
 * The bounding rectangle of two, ignoring an empty second.
 *
 * @param a The rectangle so far.
 * @param b The rectangle to add; empty (`x1 <= x0`) adds nothing.
 * @returns The union's bounds.
 */
function union(a: CellRect, b: CellRect): CellRect {
  return b.x1 <= b.x0 ? a : { x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) };
}
